import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

export function EventsListLogic({ showPast = true, onlyPublic = false, render }) {
  const [events, setEvents] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const { authToken } = useAuth();

  useEffect(() => {
    const loadEvents = async () => {
      try {
        let res;
        if (onlyPublic) {
          // Use public endpoint - no authentication needed
          console.log("Loading public events");
          res = await axios.get("/api/public/events");
        } else {
          // Use member endpoint - authentication required
          const token = localStorage.getItem("auth_token") || authToken;
          if (!token) {
            toast.error("No authentication token found");
            return;
          }
          
          console.log("Loading member events with token:", token ? "present" : "missing");
          const headers = { Authorization: `Bearer ${token}` };
          res = await axios.get("/api/public/events", { headers });
        }
        const eventsData = res.data.content || [];
        console.log("Events loaded successfully:", eventsData.length, "events");
        setEvents(eventsData);
      } catch (err) {
        console.error("Error loading events:", err);
        if (err.response) {
          console.error("Response status:", err.response.status);
          console.error("Response data:", err.response.data);
          toast.error(`Error loading events: ${err.response.data?.detail || err.response.status}`);
        } else {
          toast.error("Error loading events - network error");
        }
      }
    };

    loadEvents();
  }, [onlyPublic, authToken]);

  const toggleOpen = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day

  const sortedEvents = [...events].sort((a, b) => {
    // First sort by date
    const dateComparison = new Date(a.date) - new Date(b.date);
    if (dateComparison !== 0) return dateComparison;
    
    // If dates are the same, sort by first itinerary time
    const aTime = Array.isArray(a.time) && a.time.length > 0 && a.time[0].time ? a.time[0].time : '23:59';
    const bTime = Array.isArray(b.time) && b.time.length > 0 && b.time[0].time ? b.time[0].time : '23:59';
    
    return aTime.localeCompare(bTime);
  });

  const filteredEvents = sortedEvents.filter(e => onlyPublic ? e.public === true : e);

  // For public view, only show events that haven't been archived (day after event date)
  const visibleEvents = onlyPublic 
    ? filteredEvents.filter(e => {
        const eventDate = new Date(e.date + "T00:00");
        eventDate.setHours(0, 0, 0, 0);
        const dayAfterEvent = new Date(eventDate);
        dayAfterEvent.setDate(dayAfterEvent.getDate() + 1);
        return today < dayAfterEvent; // Show until the day after the event
      })
    : filteredEvents;

  const upcoming = visibleEvents.filter(e => {
    const eventDate = new Date(e.date + "T00:00");
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });
  
  const past = visibleEvents.filter(e => {
    const eventDate = new Date(e.date + "T00:00");
    eventDate.setHours(0, 0, 0, 0);
    return eventDate < today;
  }).reverse();

  // Pass all data + handlers to render prop
  return render({
    upcoming,
    past,
    showPast,
    openIndex,
    toggleOpen
  });
}
